import { Button, Dialog, OVERLAY_FOOTER_LAYOUT } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  CONFIRM_GATE_CONFIRM_LABEL,
  CONFIRM_GATE_KEEP_PLAYING_LABEL,
  CONFIRM_GATE_KEEP_PLAYING_LOADING_LABEL,
  CONFIRM_GATE_NAMING_TITLE,
  CONFIRM_GATE_NOT_YET_LABEL,
  CONFIRM_GATE_REVIVE_LABEL,
  CONFIRM_GATE_TITLE,
} from "./confirmGateCopy";
import { ConfirmGateNamingStep } from "./ConfirmGateNamingStep";
import { ConfirmGateOutcomeStep } from "./ConfirmGateOutcomeStep";
import type { typeConfirmGateView } from "./confirmGateState";
import type { typeAutoConfirmStatus } from "./confirmGateStatus";

export type typeContestConfirmGateDialogProps = {
  open: boolean;
  view: typeConfirmGateView;
  status: typeAutoConfirmStatus;
  namingWindowWarning: string | null;
  /** ISSUE-868. The outstanding round's label from `/round/list`, or null. */
  outstandingRoundLabel?: string | null;
  onConfirm: () => void;
  onNotYet: () => void;
  onReviveInstead: () => void;
  onSaveNamedWinners: (entryIds: readonly string[]) => void;
  /** ISSUE-1325. "Not over yet". Absent hides the control. */
  onKeepPlaying?: (() => void) | undefined;
  isConfirming?: boolean;
  isSavingNamedWinners?: boolean;
  isKeepingPlaying?: boolean;
  /** ISSUE-1309. False on a pick'em contest, whose entries are never out. */
  canReviveInstead?: boolean;
};

export const ContestConfirmGateDialog = ({
  open,
  view,
  status,
  namingWindowWarning,
  outstandingRoundLabel = null,
  onConfirm,
  onNotYet,
  onReviveInstead,
  onSaveNamedWinners,
  onKeepPlaying,
  isConfirming = false,
  isSavingNamedWinners = false,
  isKeepingPlaying = false,
  canReviveInstead = true,
}: typeContestConfirmGateDialogProps): ReactNode => {
  const footer =
    view.step === "naming" ? undefined : (
      <div className="flex w-full flex-col gap-3">
        <div className={OVERLAY_FOOTER_LAYOUT}>
          <Button variant="outline" onClick={onNotYet}>
            {CONFIRM_GATE_NOT_YET_LABEL}
          </Button>
          <Button
            variant="primary"
            loading={isConfirming}
            loadingLabel="Confirming the result"
            onClick={onConfirm}
          >
            {CONFIRM_GATE_CONFIRM_LABEL}
          </Button>
        </div>
        <p
          data-testid="confirm-gate-status"
          data-tone={status.tone}
          className={
            status.tone === "warning"
              ? "text-caption text-warning"
              : "text-caption text-ink-tertiary"
          }
        >
          {status.message}
        </p>
        {canReviveInstead ? (
          <Button variant="link" size="sm" onClick={onReviveInstead}>
            {CONFIRM_GATE_REVIVE_LABEL}
          </Button>
        ) : null}
        {/* ISSUE-1309. Keeping a pick'em contest going has nothing to revive
            and no one out, so it shows only where reviving does. */}
        {onKeepPlaying === undefined || !canReviveInstead ? null : (
          <Button
            variant="link"
            size="sm"
            data-testid="confirm-gate-keep-playing"
            loading={isKeepingPlaying}
            loadingLabel={CONFIRM_GATE_KEEP_PLAYING_LOADING_LABEL}
            onClick={onKeepPlaying}
          >
            {CONFIRM_GATE_KEEP_PLAYING_LABEL}
          </Button>
        )}
      </div>
    );

  return (
    <Dialog
      open={open}
      onOpenChange={() => undefined}
      title={
        view.step === "naming" ? CONFIRM_GATE_NAMING_TITLE : CONFIRM_GATE_TITLE
      }
      // The gate is deliberately not dismissible per R8 and the design brief's
      // platform delta table. This no-op is the whole point: `Not yet` must be
      // a deliberate button press, never a side effect of the scrim or Escape.
      isDirty={true}
      onRequestClose={() => undefined}
      hideCloseButton
      autoFocus={view.step === "naming" ? undefined : false}
      footer={footer}
    >
      {view.step === "naming" ? (
        <ConfirmGateNamingStep
          candidates={view.candidates}
          isPickem={view.isPickem === true}
          onSave={onSaveNamedWinners}
          isSaving={isSavingNamedWinners}
        />
      ) : (
        <ConfirmGateOutcomeStep
          shape={view.shape}
          winners={view.winners}
          medalName={view.medalName}
          isSeasonLastContest={view.isSeasonLastContest}
          namingWindowWarning={namingWindowWarning}
          outstandingRound={view.outstandingRound}
          outstandingRoundLabel={outstandingRoundLabel}
        />
      )}
    </Dialog>
  );
};
